/**
 * This file contains codec definitions for all portlet setting forms.
 * The reason why we have both static types and codecs is that setting form validation
 * became required late after settings themselves.
 */
import { HierarchyNode } from '@bayer-int/portletce-shared';
import * as t from 'io-ts';

import type { SettingsSource } from 'portlet/models';

import type {
    InputDefinition,
    InformationDefinition,
    TextInputDefinition,
    NumberInputDefinition,
    HierarchyCustomizationDefinition,
    TextInputWithButtonDefinition,
    TopNDefinition,
    SelectDefinition,
    SelectClearableDefinition,
    SelectNumberDefinition,
    SelectNumberClearableDefinition,
    MultiSelectDefinition,
    RichMultiSelectDefinition,
    CheckBoxDefinition,
    DrillDefinition,
    AutoDateDefinition,
    ConditionInputDefinition,
    InputBWQueryDefinition,
    ShortcutsDefinition,
    ShortcutElement,
    SettingFormInputDefinition,
    SettingFormInputValue,
    SelectCurrentViewDefinition,
    NullableTextInputDefinition,
    DatabricksConditionInputDefinition,
} from './settings';

const InformationDefinitionCodec: t.Type<InformationDefinition> = inputDefinition(
    'Information',
    t.string,
);
const TextInputDefinitionCodec: t.Type<TextInputDefinition> = inputDefinition(
    'TextInput',
    t.string,
);
const NullableTextInputDefinitionCodec: t.Type<NullableTextInputDefinition> = inputDefinition(
    'NullableTextInput',
    t.union([t.string, t.null]),
);
const NumberInputDefinitionCodec: t.Type<NumberInputDefinition> = inputDefinition(
    'NumberInput',
    t.number,
);
const HierarchyCustomizationDefinitionCodec: t.Type<HierarchyCustomizationDefinition> =
    inputDefinition('HierarchyCustomizationInput', t.array(t.string), {
        options: t.array(HierarchyNode.codec),
    });
const TextInputWithButtonDefinitionCodec: t.Type<TextInputWithButtonDefinition> = inputDefinition(
    'TextInputWithButton',
    t.string,
    {
        button: t.type({ tooltip: t.string, url: t.string }),
    },
);
const TopNDefinitionCodec: t.Type<TopNDefinition> = inputDefinition('TopN', t.string, {
    select: t.type({
        value: t.number,
        options: t.array(t.type({ key: t.number, label: t.string })),
    }),
});
const SelectClearableDefinitionCodec: t.Type<SelectClearableDefinition> = inputDefinition(
    'SelectClearable',
    t.union([t.string, t.null]),
    { options: t.array(t.type({ key: t.string, label: t.string })) },
);
const SelectNumberClearableDefinitionCodec: t.Type<SelectNumberClearableDefinition> =
    inputDefinition('SelectNumberClearable', t.union([t.number, t.null]), {
        options: t.array(t.type({ key: t.number, label: t.string })),
    });
const SelectDefinitionCodec: t.Type<SelectDefinition> = inputDefinition('Select', t.string, {
    options: t.array(t.type({ key: t.string, label: t.string })),
});
const MultiSelectDefinitionCodec: t.Type<MultiSelectDefinition> = inputDefinition(
    'MultiSelect',
    t.array(t.string),
    { options: t.array(t.type({ key: t.string, label: t.string })) },
);
const RichMultiSelectDefinitionCodec: t.Type<RichMultiSelectDefinition> = inputDefinition(
    'RichMultiSelect',
    t.array(t.string),
    { options: t.array(t.type({ key: t.string, label: t.string })) },
);
const SelectNumberDefinitionCodec: t.Type<SelectNumberDefinition> = inputDefinition(
    'SelectNumber',
    t.number,
    { options: t.array(t.type({ key: t.number, label: t.string })) },
);
const CheckBoxDefinitionCodec: t.Type<CheckBoxDefinition> = inputDefinition('CheckBox', t.boolean);

const DatabricksConditionInputValueCodec = t.type({
    field: t.string,
    order: t.union([t.literal('ASC'), t.literal('DESC')]),
    limit: t.number,
});

const DatabricksConditionInputDefinitionCodec: t.Type<DatabricksConditionInputDefinition> =
    inputDefinition('DatabricksCondition', DatabricksConditionInputValueCodec, {
        condtitionFieldOptions: t.array(t.type({ key: t.string, label: t.string })),
        conditionMaxLimit: t.number,
        conditionAllowChangeOrder: t.boolean,
    });

const ConditionInputValueCodec = t.intersection([
    t.type({ key: t.string, value: t.number }),
    t.partial({ group: t.union([t.string, t.undefined]) }),
]);

const ConditionInputDefinitionCodec: t.Type<ConditionInputDefinition> = inputDefinition(
    'Condition',
    ConditionInputValueCodec,
    {
        allowConditionChange: t.boolean,
        conditionMaxValue: t.number,
        conditionVariableName: t.string,
        options: t.array(
            t.intersection([
                t.type({ key: t.string, label: t.string }),
                t.partial({ group: t.string, defaultView: t.union([t.number, t.null]) }),
            ]),
        ),
    },
);

const DrillInputValueCodec = t.union([
    t.array(t.type({ key: t.string, order: t.number, drill: t.string })),
    t.null,
]);
const DrillDefinitionCodec: t.Type<DrillDefinition> = inputDefinition(
    'Drill',
    DrillInputValueCodec,
    { allowMultiple: t.boolean, options: t.array(t.type({ key: t.string, label: t.string })) },
);

const AutoDateValueCodec = t.union([
    t.type({
        from: t.type({ offset: t.number }),
        to: t.union([t.type({ offset: t.number }), t.null]),
    }),
    t.null,
]);
const AutoDateDefinitionCodec: t.Type<AutoDateDefinition> = inputDefinition(
    'AutoDate',
    AutoDateValueCodec,
);

const InputBWQueryValueCodec = t.union([t.type({ queryId: t.string, system: t.string }), t.null]);
const InputBWQueryDefinitionCodec: t.Type<InputBWQueryDefinition> = inputDefinition(
    'InputBWQuery',
    InputBWQueryValueCodec,
    { systems: t.array(t.string) },
);

const ShortcutElementC: t.Type<ShortcutElement> = t.type({
    key: t.string,
    label: t.string,
    value: t.union([
        t.string,
        t.number,
        t.boolean,
        t.null,
        t.array(
            t.type({
                key: t.string,
                order: t.number,
                drill: t.string,
            }),
        ),
        t.intersection([
            t.type({
                key: t.string,
                value: t.number,
            }),
            t.partial({
                group: t.string,
            }),
        ]),
        t.type({
            from: t.type({
                offset: t.number,
            }),
            to: t.union([
                t.type({
                    offset: t.number,
                }),
                t.null,
            ]),
        }),
        t.type({
            queryId: t.string,
            system: t.string,
        }),
    ]),
});
const ShortcutsValueCodec = t.type({
    left: t.array(ShortcutElementC),
    center: t.array(ShortcutElementC),
    right: t.array(ShortcutElementC),
});

const ShortcutsDefinitionCodec: t.Type<ShortcutsDefinition> = inputDefinition(
    'Shortcuts',
    ShortcutsValueCodec,
);

const SelectCurrentViewDefinitionCodec: t.Type<SelectCurrentViewDefinition> = inputDefinition(
    'SelectCurrentView',
    t.number,
    {
        options: t.array(
            t.type({
                name: t.union([t.string, t.null]),
                members: t.array(
                    t.type({
                        id: t.number,
                        label: t.string,
                        form: t.string,
                    }),
                ),
                defaultView: t.number,
            }),
        ),
    },
);

export const SettingFormInputDefinitionCodec: t.Type<SettingFormInputDefinition> = t.union(
    [
        InformationDefinitionCodec,
        HierarchyCustomizationDefinitionCodec,
        TextInputWithButtonDefinitionCodec,
        TopNDefinitionCodec,
        SelectDefinitionCodec,
        SelectClearableDefinitionCodec,
        SelectNumberDefinitionCodec,
        SelectNumberClearableDefinitionCodec,
        CheckBoxDefinitionCodec,
        TextInputDefinitionCodec,
        NullableTextInputDefinitionCodec,
        NumberInputDefinitionCodec,
        MultiSelectDefinitionCodec,
        RichMultiSelectDefinitionCodec,
        ConditionInputDefinitionCodec,
        DrillDefinitionCodec,
        AutoDateDefinitionCodec,
        InputBWQueryDefinitionCodec,
        ShortcutsDefinitionCodec,
        SelectCurrentViewDefinitionCodec,
        DatabricksConditionInputDefinitionCodec,
    ],
    'SettingFormInputDefinitionCodec',
);

const SettingsSourceCodec: t.Type<SettingsSource> = t.partial({
    appliedBy: t.union([
        t.literal('userSelection'),
        t.literal('mainConfig'),
        t.literal('viewConfig'),
    ]),
});

export const SettingFormInputValueCodec: t.Type<SettingFormInputValue & SettingsSource> =
    t.intersection(
        [
            t.type({
                value: t.union([
                    t.boolean,
                    t.string,
                    t.number,
                    t.null,
                    t.array(t.string),
                    ConditionInputValueCodec,
                    DrillInputValueCodec,
                    AutoDateValueCodec,
                    InputBWQueryValueCodec,
                    ShortcutsValueCodec,
                    DatabricksConditionInputValueCodec,
                ]),
                key: t.string,
            }),
            SettingsSourceCodec,
        ],
        'SettingFormInputValueCodec',
    );

function inputDefinition<T extends string, V extends t.Mixed, E extends Record<string, t.Mixed>>(
    type: T,
    valueCodec: V,
    additionalProperties: E,
): t.Type<InputDefinition<t.TypeOf<V>> & { type: T } & { [K in keyof E]: t.TypeOf<E[K]> }>;
function inputDefinition<T extends string, V extends t.Mixed>(
    type: T,
    valueCodec: V,
): t.Type<InputDefinition<t.TypeOf<V>> & { type: T }>;
function inputDefinition<T extends string, V extends t.Mixed, E extends Record<string, t.Mixed>>(
    type: T,
    valueCodec: V,
    additionalProperties: E = {} as E,
): t.Type<InputDefinition<t.TypeOf<V>> & { type: T } & { [K in keyof E]: t.TypeOf<E[K]> }> {
    const required = t.type({
        type: t.literal(type),
        key: t.string,
        label: t.string,
        value: valueCodec,
        ...additionalProperties,
    });

    const optional = t.partial({
        changeable: t.boolean,
        scope: t.union([t.literal('user'), t.literal('designer'), t.literal('none')]),
        lazy: t.union([t.literal('uninitialized'), t.literal('loading'), t.literal('initialized')]),
    });

    return t.intersection([required, optional], `InputDefinition<${type}>`);
}
